import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { assessmentScheduleAPI } from '../../api/assessmentScheduleApi';
import * as S from './Assessment.styles';

const Assessment = () => {
  const [formData, setFormData] = useState({
    studentName: '',
    studentLevel: '',
    school: '',
    email: '',
    phone: '',
    campus: '',
    assessmentDate: null, // 改为null以配合DatePicker
    assessmentTime: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [campusSchedules, setCampusSchedules] = useState({});
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(true);
  const [scheduleError, setScheduleError] = useState(null);

  const studentLevels = [
    'Pre-primary',
    'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6',
    'Year 7'
  ];

  const campuses = [
    'Claremont',
    'Osborne Park',
    'Victoria Park'
  ];

  // 从API获取校区营业时间配置
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        setIsLoadingSchedules(true);
        setScheduleError(null);
        
        // 直接从API获取最新数据
        console.log('Fetching schedules from API...');
        const schedules = await assessmentScheduleAPI.getActiveSchedules();
        console.log('Fetched schedules from API:', schedules);
        setCampusSchedules(schedules);
      } catch (error) {
        console.error('Error fetching assessment schedules:', error);
        setScheduleError('Unable to load booking schedules. Using default schedules.');
        
        // 使用默认配置作为后备方案
        const defaultSchedules = {
          'Claremont': {
            '1': { start: '14:00', end: '18:00' },
            '2': null,
            '3': { start: '14:00', end: '18:00' },
            '4': { start: '14:00', end: '18:00' },
            '5': null,
            '6': { start: '09:30', end: '18:00' },
            '0': { start: '09:30', end: '15:00' }
          },
          'Osborne Park': {
            '1': { start: '15:00', end: '20:30' },
            '2': { start: '15:00', end: '20:30' },
            '3': { start: '15:30', end: '20:30' },
            '4': { start: '15:00', end: '20:30' },
            '5': { start: '15:00', end: '20:30' },
            '6': { start: '09:00', end: '20:30' },
            '0': { start: '09:00', end: '20:30' }
          },
          'Victoria Park': {
            '1': { start: '15:00', end: '18:00' },
            '2': { start: '15:00', end: '18:00' },
            '3': { start: '15:00', end: '18:00' },
            '4': { start: '15:00', end: '18:00' },
            '5': { start: '15:00', end: '18:00' },
            '6': { start: '12:00', end: '17:00' },
            '0': { start: '10:00', end: '17:00' }
          }
        };
        setCampusSchedules(defaultSchedules);
      } finally {
        setIsLoadingSchedules(false);
      }
    };

    fetchSchedules();
  }, []);

  // 获取校区最低提前预约小时数
  const getMinAdvanceHours = (campus) => {
    if (campus === 'Victoria Park') return 24;
    return 2;
  };

  // 生成时间选项
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const generateTimeSlots = useCallback((start, end, selectedDate) => {
    const times = [];
    const startHour = parseInt(start.split(':')[0]);
    const startMin = parseInt(start.split(':')[1]);
    const endHour = parseInt(end.split(':')[0]);
    const endMin = parseInt(end.split(':')[1]);

    // 如果是今天，需要检查最早可预约时间（当前时间+2小时）
    // 使用东八区时间进行比较
    const now = new Date();
    
    // 获取东八区当前时间
    const awstNow = new Date(now.toLocaleString("en-US", {timeZone: "Australia/Perth"}));
    
    const advanceHours = getMinAdvanceHours(formData.campus);
    const advanceLater = new Date(awstNow.getTime() + advanceHours * 60 * 60 * 1000);

    const isSameDay = selectedDate &&
      selectedDate.getDate() === advanceLater.getDate() &&
      selectedDate.getMonth() === advanceLater.getMonth() &&
      selectedDate.getFullYear() === advanceLater.getFullYear();

    let earliestHour = startHour;
    let earliestMin = startMin;

    if (isSameDay) {
      // 计算最早可预约时间（当前时间+提前小时数）
      const twoHoursLater = advanceLater;
      const minHour = twoHoursLater.getHours();
      const minMin = twoHoursLater.getMinutes();
      
      // 向上取整到下个30分钟时间段
      let adjustedMin = minMin <= 30 ? 30 : 0;
      let adjustedHour = minMin > 30 ? minHour + 1 : minHour;
      
      // 如果分钟数为0且不是整点30分，调整到30分
      if (minMin > 0 && minMin <= 30) {
        adjustedMin = 30;
      } else if (minMin > 30) {
        adjustedMin = 0;
        adjustedHour = minHour + 1;
      }
      
      // 如果调整后的时间比营业开始时间晚，使用调整后的时间
      if (adjustedHour > earliestHour || (adjustedHour === earliestHour && adjustedMin > earliestMin)) {
        earliestHour = adjustedHour;
        earliestMin = adjustedMin;
      }
    }

    let currentHour = earliestHour;
    let currentMin = earliestMin;

    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const timeString = `${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`;
      times.push(timeString);
      
      currentMin += 30; // 每30分钟一个时间段
      if (currentMin >= 60) {
        currentMin = 0;
        currentHour++;
      }
    }

    return times;
  }, [formData.campus]);

  // 检查日期是否可用
  const isDateAvailable = (date, campus) => {
    if (!campus || !date) return false;
    
    const dayOfWeek = date.getDay();
    const schedule = campusSchedules[campus];
    
    return schedule && schedule[dayOfWeek.toString()] !== null;
  };

  // 过滤可用日期
  const filterDate = (date) => {
    if (!formData.campus) return false;

    // 不能选择今天之前的日期（使用东八区时间）
    const now = new Date();
    const awstNow = new Date(now.toLocaleString("en-US", {timeZone: "Australia/Perth"}));

    const todayMidnight = new Date(awstNow);
    todayMidnight.setHours(0, 0, 0, 0);

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    if (checkDate <= todayMidnight) return false;

    // 根据校区提前预约要求，屏蔽提前时间内的日期
    const advanceHours = getMinAdvanceHours(formData.campus);
    const cutoff = new Date(awstNow.getTime() + advanceHours * 60 * 60 * 1000);
    const cutoffDate = new Date(cutoff);
    cutoffDate.setHours(0, 0, 0, 0);
    if (checkDate < cutoffDate) return false;
    
    // 不能选择3个月后的日期
    const maxDate = new Date(awstNow);
    maxDate.setMonth(maxDate.getMonth() + 3);
    if (date > maxDate) return false;
    
    return isDateAvailable(date, formData.campus);
  };

  // 更新可用时间
  useEffect(() => {
    if (formData.campus && formData.assessmentDate && !isLoadingSchedules) {
      const dayOfWeek = formData.assessmentDate.getDay();
      const schedule = campusSchedules[formData.campus];
      
      if (schedule && schedule[dayOfWeek.toString()]) {
        const { start, end } = schedule[dayOfWeek.toString()];
        const times = generateTimeSlots(start, end, formData.assessmentDate);
        setAvailableTimes(times);
        
        // 使用回调函数来避免依赖问题
        setFormData(prev => {
          if (prev.assessmentTime && !times.includes(prev.assessmentTime)) {
            return {
              ...prev,
              assessmentTime: ''
            };
          }
          return prev;
        });
      } else {
        setAvailableTimes([]);
        setFormData(prev => ({
          ...prev,
          assessmentTime: ''
        }));
      }
    } else {
      setAvailableTimes([]);
      setFormData(prev => ({
        ...prev,
        assessmentTime: ''
      }));
    }
  }, [formData.campus, formData.assessmentDate, campusSchedules, isLoadingSchedules, generateTimeSlots]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // 如果改变校区，清空日期和时间
    if (name === 'campus') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        assessmentDate: null,
        assessmentTime: ''
      }));
    }
    else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // 清除该字段的错误信息
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 处理日期变化
  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      assessmentDate: date,
      assessmentTime: '' // 清空时间选择
    }));
    
    // 清除日期错误信息
    if (errors.assessmentDate) {
      setErrors(prev => ({
        ...prev,
        assessmentDate: ''
      }));
    }
  };

  // 判断是否为预约制校区
  const isAppointmentOnlyCampus = formData.campus === 'Osborne Park';

  const validateForm = () => {
    const newErrors = {};

    if (!formData.studentName.trim()) {
      newErrors.studentName = 'Student name is required';
    }

    if (!formData.studentLevel) {
      newErrors.studentLevel = 'Student level is required';
    }

    if (!formData.school.trim()) {
      newErrors.school = 'Current school is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.campus) {
      newErrors.campus = 'Campus selection is required';
    }

    // 只有非预约制校区才验证日期和时间
    if (!isAppointmentOnlyCampus) {
      if (!formData.assessmentDate) {
        newErrors.assessmentDate = 'Assessment date is required';
      } else if (!isDateAvailable(formData.assessmentDate, formData.campus)) {
        newErrors.assessmentDate = 'Selected date is not available for this campus';
      }

      if (!formData.assessmentTime) {
        newErrors.assessmentTime = 'Assessment time is required';
      } else {
        // 验证选择的时间是否仍然有效
        const now = new Date();
        const awstNow = new Date(now.toLocaleString("en-US", {timeZone: "Australia/Perth"}));
        const advanceHours = getMinAdvanceHours(formData.campus);
        const advanceCutoff = new Date(awstNow.getTime() + advanceHours * 60 * 60 * 1000);

        const selectedTime = formData.assessmentTime.split(':');
        const selectedDateTime = new Date(formData.assessmentDate);
        selectedDateTime.setHours(parseInt(selectedTime[0]), parseInt(selectedTime[1]), 0, 0);

        if (selectedDateTime < advanceCutoff) {
          newErrors.assessmentTime = `Please select a time at least ${advanceHours} hours from now`;
        }

        // 检查选择的时间是否在可用时间列表中
        if (!availableTimes.includes(formData.assessmentTime)) {
          newErrors.assessmentTime = 'Selected time is no longer available';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // 按照东八区时间格式化日期 - 简化版本
      const formatDateAsAWST = (date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // 创建东八区的完整时间戳 - 简化版本
      const createAWSTDateTime = (date, time) => {
        const [hours, minutes] = time.split(':');
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        
        // 直接构造东八区时间字符串
        const dateTimeString = `${year}-${month}-${day}T${hours}:${minutes}:00+08:00`;
        return dateTimeString;
      };

      // Use Gmail API endpoint (production ready)
      const apiEndpoint = 'https://1m2ws49bzk.execute-api.ap-southeast-2.amazonaws.com/test';
      
      console.log('Using Gmail API endpoint for all email processing');

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          studentName: formData.studentName,
          studentLevel: formData.studentLevel,
          school: formData.school,
          email: formData.email,
          phone: formData.phone,
          campus: formData.campus,
          // Osborne Park 使用预约制特殊值
          assessmentDate: isAppointmentOnlyCampus ? 'By Appointment' : formatDateAsAWST(formData.assessmentDate),
          assessmentTime: isAppointmentOnlyCampus ? 'TBA' : formData.assessmentTime,
          assessmentDateTime: isAppointmentOnlyCampus ? null : createAWSTDateTime(formData.assessmentDate, formData.assessmentTime),
          submittedAt: new Date().toISOString(),
          timezone: 'AWST',
          timezoneOffset: '+08:00',
          isAppointmentOnly: isAppointmentOnlyCampus
        })
      });
      if (response.ok) {
        const result = await response.json();
        console.log('Success:', result);
        alert('Assessment booking submitted successfully!');
        
        // 重置表单
        setFormData({
          studentName: '',
          studentLevel: '',
          school: '',
          email: '',
          phone: '',
          campus: '',
          assessmentDate: null,
          assessmentTime: ''
        });
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Server error:', errorData);
        alert(`Failed to submit: ${errorData.message || 'Server error'}`);
      }
    } catch (error) {
      console.error('Network error:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <S.Container>
      <Helmet>
        <title>Book a Free Assessment - Uplus Education</title>
        <meta name="description" content="Book a free assessment with Uplus Education" />
      </Helmet>
      
      <S.Title>Book a Free Assessment</S.Title>
      
      {scheduleError && (
        <S.ErrorMessage style={{ marginBottom: '20px', textAlign: 'center' }}>
          {scheduleError}
        </S.ErrorMessage>
      )}
      
      <S.Form onSubmit={handleSubmit}>
        <S.FormGroup>
          <S.Label htmlFor="studentName">Student Name *</S.Label>
          <S.Input
            type="text"
            id="studentName"
            name="studentName"
            value={formData.studentName}
            onChange={handleChange}
            placeholder="Enter student's full name"
          />
          {errors.studentName && <S.ErrorMessage>{errors.studentName}</S.ErrorMessage>}
        </S.FormGroup>

        <S.FormGroup>
          <S.Label htmlFor="studentLevel">Student Level *</S.Label>
          <S.Select
            id="studentLevel"
            name="studentLevel"
            value={formData.studentLevel}
            onChange={handleChange}
          >
            <option value="">Select student level</option>
            {studentLevels.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </S.Select>
          {errors.studentLevel && <S.ErrorMessage>{errors.studentLevel}</S.ErrorMessage>}
        </S.FormGroup>

        <S.FormGroup>
          <S.Label htmlFor="school">Current School *</S.Label>
          <S.Input
            type="text"
            id="school"
            name="school"
            value={formData.school}
            onChange={handleChange}
            placeholder="Enter student's current school"
          />
          {errors.school && <S.ErrorMessage>{errors.school}</S.ErrorMessage>}
        </S.FormGroup>

        <S.FormGroup>
          <S.Label htmlFor="email">Email Address *</S.Label>
          <S.Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email address"
          />
          {errors.email && <S.ErrorMessage>{errors.email}</S.ErrorMessage>}
        </S.FormGroup>

        <S.FormGroup>
          <S.Label htmlFor="phone">Phone Number *</S.Label>
          <S.Input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Enter your phone number"
          />
          {errors.phone && <S.ErrorMessage>{errors.phone}</S.ErrorMessage>}
        </S.FormGroup>

        <S.FormGroup>
          <S.Label htmlFor="campus">Campus *</S.Label>
          <S.Select
            id="campus"
            name="campus"
            value={formData.campus}
            onChange={handleChange}
          >
            <option value="">Select campus</option>
            {campuses.map(campus => (
              <option key={campus} value={campus}>{campus}</option>
            ))}
          </S.Select>
          {errors.campus && <S.ErrorMessage>{errors.campus}</S.ErrorMessage>}
        </S.FormGroup>

        <S.FormGroup>
          <S.Label>Assessment Time {!isAppointmentOnlyCampus && '*'}</S.Label>
          {isAppointmentOnlyCampus ? (
            <S.AppointmentOnlyInfo>
              <strong>Appointment Only</strong>
              Assessment times for Osborne Park are by appointment only. After you submit this form, our team will contact you to arrange a suitable time.
            </S.AppointmentOnlyInfo>
          ) : (
            <S.DateTimeGroup>
              <div>
                <S.DatePickerWrapper>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={formData.assessmentDate}
                      onChange={handleDateChange}
                      shouldDisableDate={(date) => !filterDate(date)}
                      disabled={!formData.campus || isLoadingSchedules}
                      slotProps={{
                        textField: {
                          placeholder: 'Select date',
                          className: 'date-picker-input',
                        },
                      }}
                      format="dd-MM-yyyy"
                    />
                  </LocalizationProvider>
                </S.DatePickerWrapper>
                {errors.assessmentDate && <S.ErrorMessage>{errors.assessmentDate}</S.ErrorMessage>}
                {formData.campus && !isLoadingSchedules && (
                  <S.DateHelper>
                    {(() => {
                      const schedule = campusSchedules[formData.campus];
                      if (!schedule) return 'Schedule not available';

                      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                      const availableDays = [];

                      for (let i = 0; i < 7; i++) {
                        const daySchedule = schedule[i.toString()];
                        if (daySchedule && daySchedule.start && daySchedule.end) {
                          availableDays.push(`${days[i]} (${daySchedule.start}-${daySchedule.end})`);
                        }
                      }

                      return availableDays.length > 0
                        ? `Available: ${availableDays.join(', ')}`
                        : 'No available booking times for this campus';
                    })()}
                    <br />
                    <strong>Note: Bookings must be made at least {getMinAdvanceHours(formData.campus)} hours in advance</strong>
                  </S.DateHelper>
                )}
              </div>
              <div>
                <S.Select
                  name="assessmentTime"
                  value={formData.assessmentTime}
                  onChange={handleChange}
                  disabled={!formData.assessmentDate || availableTimes.length === 0}
                >
                  <option value="">Select time</option>
                  {availableTimes.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </S.Select>
                {errors.assessmentTime && <S.ErrorMessage>{errors.assessmentTime}</S.ErrorMessage>}
              </div>
            </S.DateTimeGroup>
          )}
        </S.FormGroup>

        <S.SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Book Assessment'}
        </S.SubmitButton>
      </S.Form>
    </S.Container>
  );
};

export default Assessment;